/**
 * @file src/core/loaders/AssetLoader.js
 * ctx.assets — tải asset bằng KHOÁ LOGIC, không bằng đường dẫn.
 *
 * Vì sao tiết KHÔNG được viết đường dẫn file:
 *   URL thật có content hash trong tên (main.9f2c81a4.glb) để cache immutable
 *   1 năm. Hardcode đường dẫn sẽ vỡ ngay lần sửa asset đầu tiên.
 *
 * Manifest → URL:  assetRoot + '/' + path   (hoặc path tuyệt đối nếu shared: true)
 */

import * as THREE from 'three';
import { createGLTFLoader } from './gltf.js';

/**
 * @typedef {Object} AssetEntry
 * @property {'gltf'|'ktx2'|'texture'|'audio'|'json'} type
 * @property {string} path
 * @property {boolean} [shared]
 * @property {boolean} [critical]
 * @property {string} [sha256]
 * @property {number} [bytes]
 * @property {Record<string, any>} [meta]
 * @property {Record<string, any>} [license]
 * @property {{path: string, bytes?: number}} [fallback]
 */

/**
 * @typedef {Object} AssetManifest
 * @property {string} scopeId
 * @property {string} assetRoot
 * @property {Record<string, AssetEntry>} assets
 */

/**
 * @typedef {Object} AssetApi
 * @property {(key: string) => Promise<{scene: THREE.Group, animations: THREE.AnimationClip[]}>} gltf
 * @property {(key: string) => Promise<THREE.Texture>} texture
 * @property {(key: string) => Promise<THREE.Texture>} ktx2
 * @property {(key: string) => Promise<AudioBuffer>} audio
 * @property {<T=unknown>(key: string) => Promise<T>} json
 * @property {(key: string) => string} url
 * @property {(keys: string[]) => void} prefetch
 * @property {() => {loaded: number, total: number, bytes: number}} progress
 */

/**
 * @param {Object} opts
 * @param {AssetManifest} opts.manifest
 * @param {import('three').LoadingManager} opts.manager
 * @param {import('three').WebGLRenderer} opts.renderer
 * @param {<T>(o: T) => T} opts.track
 * @param {(code: string, o: any) => void} opts.reportError
 * @param {string} [opts.baseUrl='/']
 * @returns {AssetApi}
 */
export function createAssetLoader(opts) {
  const { manifest, manager, renderer, track, reportError } = opts;

  /**
   * LỌC BẤT ĐẲNG HƯỚNG TỐI ĐA cho mọi texture — theo chính sách "chất lượng hình ảnh cao
   * nhất". Trước đây KHÔNG chỗ nào đặt anisotropy, nên three dùng mặc định 1: texture
   * nhìn CHẾCH hoặc nhìn xa bị nhoè thành vệt, rõ nhất ở mặt đất/mặt nước trải rộng
   * (cảnh sa mạc của tiết Ai Cập là đúng trường hợp tệ nhất).
   * Đây là mức tăng chất lượng gần như MIỄN PHÍ: nó chỉ đổi cách lấy mẫu mipmap, không
   * tăng dung lượng tải và tốn rất ít GPU so với việc tăng độ phân giải texture.
   * getMaxAnisotropy() đọc giới hạn THẬT của GPU (thường 16), nên không bao giờ đặt quá.
   */
  const maxAniso = () => renderer.capabilities.getMaxAnisotropy();

  /** @param {any} tex @returns {any} */
  function sharpen(tex) {
    if (!tex || !tex.isTexture) return tex;
    tex.anisotropy = maxAniso();
    tex.needsUpdate = true;
    return tex;
  }

  /**
   * Áp anisotropy cho MỌI texture trong một cây object đã nạp. Texture của .glb do
   * GLTFLoader tự tạo nên không đi qua hàm texture() bên dưới — phải quét lại ở đây.
   * @param {any} root
   */
  function sharpenTree(root) {
    /** @type {string[]} */
    const slots = [
      'map',
      'normalMap',
      'roughnessMap',
      'metalnessMap',
      'aoMap',
      'emissiveMap',
      'bumpMap',
      'displacementMap',
      'alphaMap',
      'clearcoatMap',
      'clearcoatNormalMap',
      'clearcoatRoughnessMap',
      'sheenColorMap',
      'specularMap',
      'specularColorMap',
      'specularIntensityMap',
      'iridescenceMap',
      'transmissionMap',
      'thicknessMap',
    ];
    root.traverse((/** @type {any} */ o) => {
      if (!o.isMesh || !o.material) return;
      for (const m of Array.isArray(o.material) ? o.material : [o.material]) {
        if (!m) continue;
        for (const s of slots) if (m[s]) sharpen(m[s]);
      }
    });
  }
  const baseUrl = opts.baseUrl ?? '/';

  /** @type {Map<string, Promise<any>>} */
  const cache = new Map();
  let loaded = 0;
  let bytes = 0;

  /** @param {string} key @returns {AssetEntry} */
  function entry(key) {
    const e = manifest.assets[key];
    if (!e) {
      const known = Object.keys(manifest.assets).join(', ');
      throw new Error(
        `[assets] không có khoá "${key}" trong assets.manifest.json. Khoá có sẵn: ${known}`,
      );
    }
    return e;
  }

  /** @param {string} key */
  function url(key) {
    const e = entry(key);
    const p = e.shared ? e.path : `${manifest.assetRoot}/${e.path}`;
    return new URL(p.replace(/^\/+/, ''), new URL(baseUrl, location.href)).toString();
  }

  /**
   * Loader dùng chung cho mọi .glb của tiết. Đã có sẵn meshopt + Draco + KTX2 —
   * KHÔNG còn phụ thuộc manifest khai `meta.compression`, xem lý do trong gltf.js.
   * @type {Promise<import('three/addons/loaders/GLTFLoader.js').GLTFLoader> | null}
   */
  let gltfLoaderP = null;
  function gltfLoader() {
    if (!gltfLoaderP) gltfLoaderP = createGLTFLoader({ manager, renderer });
    return gltfLoaderP;
  }

  /**
   * @template T
   * @param {string} key
   * @param {() => Promise<T>} fn
   * @returns {Promise<T>}
   */
  function once(key, fn) {
    const hit = cache.get(key);
    if (hit) return hit;
    const p = fn()
      .then((v) => {
        loaded++;
        bytes += entry(key).bytes ?? 0;
        return v;
      })
      .catch((err) => {
        cache.delete(key);
        const e = entry(key);
        reportError(e.critical ? 'ASSET_404' : 'ASSET_TIMEOUT', {
          fatal: Boolean(e.critical),
          retryable: true,
          message: 'asset.loadFailed',
          detail: String(err?.message ?? err),
          assetKey: key,
        });
        throw err;
      });
    cache.set(key, p);
    return p;
  }

  /** Track mọi geometry/material/texture bên trong một cây model. */
  function trackTree(/** @type {THREE.Object3D} */ root) {
    root.traverse((o) => {
      const any = /** @type {any} */ (o);
      if (!any.isMesh && !any.isLine && !any.isPoints && !any.isSprite) return;
      if (any.geometry) track(any.geometry);
      for (const m of Array.isArray(any.material) ? any.material : [any.material]) {
        if (!m) continue;
        track(m);
        for (const k in m) {
          const v = m[k];
          if (v && v.isTexture) track(v);
        }
      }
    });
    return root;
  }

  return {
    gltf(key) {
      return once(key, async () => {
        const loader = await gltfLoader();
        const gltf = await loader.loadAsync(url(key));
        // Texture trong .glb do GLTFLoader tạo, không qua texture() bên dưới → quét lại.
        sharpenTree(gltf.scene);
        trackTree(gltf.scene);
        return { scene: gltf.scene, animations: gltf.animations ?? [] };
      });
    },

    texture(key) {
      return once(key, async () => {
        const loader = new THREE.TextureLoader(manager);
        const tex = await loader.loadAsync(url(key));
        tex.colorSpace =
          entry(key).meta?.srgb === false ? THREE.NoColorSpace : THREE.SRGBColorSpace;
        tex.generateMipmaps = true;
        sharpen(tex);
        return track(tex);
      });
    },

    ktx2(key) {
      return once(key, async () => {
        const { KTX2Loader } = await import('three/addons/loaders/KTX2Loader.js');
        const loader = new KTX2Loader(manager)
          .setTranscoderPath('/_core/vendor/basis/')
          .detectSupport(renderer);
        const tex = await loader.loadAsync(url(key));
        tex.colorSpace =
          entry(key).meta?.srgb === false ? THREE.NoColorSpace : THREE.SRGBColorSpace;
        track(tex);
        loader.dispose();
        sharpen(tex);
        return tex;
      });
    },

    audio(key) {
      return once(key, async () => {
        const res = await fetch(url(key));
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = await res.arrayBuffer();
        const AC = window.AudioContext ?? /** @type {any} */ (window).webkitAudioContext;
        const ctxAudio = new AC();
        const decoded = await ctxAudio.decodeAudioData(buf);
        await ctxAudio.close();
        return decoded;
      });
    },

    json(key) {
      return once(key, async () => {
        const res = await fetch(url(key));
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      });
    },

    url,

    prefetch(keys) {
      for (const k of keys) {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = url(k);
        link.as = 'fetch';
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      }
    },

    progress() {
      return { loaded, total: Object.keys(manifest.assets).length, bytes };
    },
  };
}
